import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../../ui/tooltip';
import type { ExternalIntelCompositeRow } from '../../../api/intelClient';

interface ProvenanceTooltipProps {
  row: ExternalIntelCompositeRow;
  children: React.ReactNode;
}

/**
 * Hover tooltip exposing the WorldMonitor composite provenance metadata. NEVER exposes
 * the underlying numeric `composite_value` — the response strips it and
 * this component never receives it as a prop (FR-1.2.a / FR-1.2.d AC).
 *
 * Surfaces only:
 *  - producer (e.g., "lattice")
 *  - producer_baseline_v (e.g., "v3.2")
 *  - emitted_at / fetched_at timestamps (ISO 8601, rendered verbatim)
 *  - freshness_tier (fresh|aging|stale|expired)
 *  - external_id (e.g., "JP:1777981453639")
 *  - composed_of (how many upstream signals composed this band)
 *  - tooltip_text (server-supplied narrative, when present)
 *
 * Region is not in the tooltip — it's the row's leading label.
 *
 * Karpathy note: tooltips for external data must show provenance, not
 * just data. The whole point of the panel is that WorldMonitor composites
 * are external + unverified — the tooltip is where that "where did this
 * come from" question gets answered.
 */
export function ProvenanceTooltip({ row, children }: ProvenanceTooltipProps) {
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side="left"
          align="start"
          className="max-w-[280px] bg-[var(--e-bg-elevated)] text-[var(--e-text-primary)] border border-[var(--e-border-secondary)] p-3 font-mono text-[10px] leading-relaxed"
          data-testid="external-intel-provenance-tooltip"
        >
          <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
            <ProvRow label="producer" value={row.producer} />
            <ProvRow label="baseline" value={row.producer_baseline_v} />
            <ProvRow label="emitted" value={row.emitted_at} mono />
            <ProvRow label="fetched" value={row.fetched_at} mono />
            <ProvRow label="freshness" value={row.freshness_tier} />
            <ProvRow label="external id" value={row.external_id} mono />
            <ProvRow
              label="composed of"
              value={row.composed_of !== null ? `${row.composed_of} signals` : null}
            />
          </dl>
          {row.tooltip_text && (
            <p className="mt-2 pt-2 border-t border-[var(--e-border-secondary)]/40 text-[var(--e-text-secondary)] italic">
              {row.tooltip_text}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ProvRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
}) {
  return (
    <>
      <dt className="uppercase tracking-wider text-[var(--e-text-muted)]">
        {label}
      </dt>
      <dd className={mono ? 'font-mono' : ''}>
        {value ?? <span className="text-[var(--e-text-muted)]">—</span>}
      </dd>
    </>
  );
}
